import React, { useState, useEffect } from 'react';
import { MapPin, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Location } from '@/types';
import { apiService } from '@/services/api';
import toast from 'react-hot-toast';

interface LocationSelectorProps {
  selectedLocationId: string | null;
  onLocationChange: (locationId: string | null) => void;
}

const LocationSelector: React.FC<LocationSelectorProps> = ({
  selectedLocationId,
  onLocationChange,
}) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [expandedLocations, setExpandedLocations] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await apiService.get<Location[]>('/locations');
      setLocations(response || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Fehler beim Laden der Standorte');
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  locations.find(loc => loc.id === selectedLocationId);

  // Funktion zum Erstellen des vollständigen Standort-Pfads
  const getLocationPath = (locationId: string | null): string => {
    if (!locationId) return 'Alle Standorte';
    
    const path: string[] = [];
    let currentLocation = locations.find(loc => loc.id === locationId);
    
    // Sammle alle übergeordneten Standorte
    while (currentLocation) {
      path.unshift(currentLocation.name);
      currentLocation = locations.find(loc => loc.id === currentLocation?.parentId);
    }
    
    return path.join(' → ');
  };

  const handleLocationSelect = (locationId: string | null) => {
    onLocationChange(locationId);
    setIsOpen(false);
  };

  const toggleExpanded = (locationId: string) => {
    const newExpanded = new Set(expandedLocations);
    if (newExpanded.has(locationId)) {
      newExpanded.delete(locationId);
    } else {
      newExpanded.add(locationId);
    }
    setExpandedLocations(newExpanded);
  };

  const getChildLocations = (parentId: string | null) => {
    return locations.filter(loc => loc.parentId === parentId);
  };

  const renderLocationTree = (parentId: string | null, level: number = 0) => {
    const childLocations = getChildLocations(parentId);
    
    return childLocations.map(location => {
      const hasChildren = locations.some(loc => loc.parentId === location.id);
      const isExpanded = expandedLocations.has(location.id);
      
      return (
        <div key={location.id}>
          <div
            className={`w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center cursor-pointer ${
              selectedLocationId === location.id ? 'bg-muted font-medium' : ''
            }`}
            style={{ paddingLeft: `${12 + level * 16}px` }}
            onClick={() => handleLocationSelect(location.id)}
          >
            {hasChildren && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpanded(location.id);
                }}
                className="mr-1 h-4 w-4 flex items-center justify-center cursor-pointer hover:bg-muted rounded"
              >
                {isExpanded ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
              </div>
            )}
            {!hasChildren && <div className="w-4 mr-1" />}
            <MapPin className="h-3 w-3 mr-2 text-muted-foreground" />
            {location.name}
          </div>
          
          {hasChildren && isExpanded && (
            <div>
              {renderLocationTree(location.id, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2 text-sm text-muted-foreground">
        <MapPin className="h-4 w-4" />
        <span>Lade Standorte...</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 h-8 px-3 max-w-xs"
        title={getLocationPath(selectedLocationId)}
      >
        <MapPin className="h-4 w-4 flex-shrink-0" />
        <span className="text-sm truncate">
          {getLocationPath(selectedLocationId)}
        </span>
        <ChevronDown className="h-4 w-4 flex-shrink-0" />
      </Button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-80 bg-background border rounded-md shadow-lg z-50 max-h-96 overflow-y-auto">
          <div className="py-1">
            <button
              onClick={() => handleLocationSelect(null)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center ${
                !selectedLocationId ? 'bg-muted font-medium' : ''
              }`}
            >
              <MapPin className="h-3 w-3 mr-2 text-muted-foreground" />
              Alle Standorte
            </button>
            {renderLocationTree(null)}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSelector;
